<template>
  <section :id class="paragraph-employee-list">
    <ul
      v-if="options.departments && tabs.length > 1"
      class="container mb-6 flex gap-2 overflow-auto tablet:flex-wrap tablet:overflow-hidden"
    >
      <li
        v-for="tab in tabs"
        :key="tab.id"
        :class="{ 'is-active': activeDepartment === tab.id }"
      >
        <a
          :href="'#' + tab.id"
          class="inline-block whitespace-nowrap rounded-full border border-solid border-muted px-3 py-1 text-size-6 font-bold text-muted"
          :class="{
            'border-primary bg-primary text-scheme-main':
              tab.id === activeDepartment,
            'hover:bg-scheme-main-bis': tab.id !== activeDepartment,
          }"
          @click.prevent="onTabClick(tab.id)"
        >
          {{ tab.label }}
        </a>
      </li>
    </ul>
    <div
      class="employee-list container"
      :class="{
        'is-half': options.columns === 'half',
        'is-third': options.columns === 'third',
        'is-quarter': options.columns === 'quarter',
      }"
    >
      <BlokkliField
        name="field_paragraphs"
        :list="filteredParagraphs"
        tag="ul"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import type {
  ParagraphEmployeeFragment,
  ParagraphEmployeeListFragment,
} from '#graphql-operations'
import { filterNullArray, onlyUnique } from '~/helpers'
import { notNullish } from '@vueuse/core'

const props = withDefaults(
  defineProps<{
    paragraphs?: ParagraphEmployeeListFragment['paragraphs']
  }>(),
  {
    paragraphs: () => [],
  },
)

const { index, siblings, options } = defineBlokkli({
  bundle: 'employee_list',
  globalOptions: ['bkHiddenGlobally', 'publicationChannels'],
  options: {
    columns: {
      type: 'radios',
      label: 'Columns',
      default: 'third',
      options: {
        quarter: '25%',
        third: '33%',
        half: '50%',
      },
    },
    departments: {
      type: 'checkbox',
      label: 'Show departments filter',
      default: false,
    },
  },
  propsFieldMapping: {
    paragraphs: { type: 'field', name: 'field_paragraphs' },
  },
})

const id = useParagraphAnchorId(index, siblings)

// Department filtering
const activeDepartment = useQueryString('department', 'all')

function onTabClick(id: string) {
  activeDepartment.value = id
}

// Fetch departments for labels
const activeEmployees = computed(() =>
  filterNullArray(props.paragraphs).filter(
    (v) =>
      v?.props &&
      'employee' in v.props &&
      (v.props as ParagraphEmployeeFragment)?.employee?.status,
  ),
)
const { data: departmentsData } = await useAsyncGraphqlQuery(
  'departments',
  {},
  {
    graphqlCaching: {
      client: true,
    },
  },
)

const departmentLabels = computed(() => {
  return (
    departmentsData.value?.data.departments.items?.reduce<
      Record<string, string>
    >((acc, v) => {
      if (v && 'name' in v && v.tid && v.name) {
        acc[v.tid] = v.name
      }
      return acc
    }, {}) || {}
  )
})

// Extract departments from active employees only
const paragraphsWithDepartments = computed(() => {
  return activeEmployees.value.map((paragraph) => {
    let departments: string[] = []
    if (paragraph.props && 'employee' in paragraph.props) {
      departments =
        paragraph.props.employee?.department
          ?.map((d: { id?: string } | null) => d?.id)
          .filter(notNullish) || []
    }
    return { paragraph, departments }
  })
})

const availableDepartments = computed<string[]>(() =>
  paragraphsWithDepartments.value
    .flatMap((v) => v.departments)
    .filter(notNullish)
    .filter(onlyUnique),
)

const { $texts } = useEasyTexts()

const tabs = computed(() => {
  const items = availableDepartments.value.map((id) => ({
    id,
    label: departmentLabels.value[id] || id,
  }))

  return [
    {
      id: 'all',
      label: $texts('all'),
    },
    ...items,
  ]
})

const filteredParagraphs = computed(() => {
  const items = activeEmployees.value
  if (activeDepartment.value === 'all') {
    return items
  }

  return paragraphsWithDepartments.value
    .filter((v) => v.departments.includes(activeDepartment.value))
    .map((v) => v.paragraph)
})
</script>

<style lang="postcss">
.paragraph-employee-list {
  .employee-list > ul {
    @apply grid gap-4;
    grid-template-columns: repeat(2, 1fr);

    @screen tablet {
      grid-gap: theme('spacing.size-4') theme('spacing.size-6');
    }
    @screen desktop {
      grid-gap: theme('spacing.size-2') theme('spacing.size-6');
    }
  }

  /* 50% - 2 columns */
  .employee-list.is-half > ul {
    grid-template-columns: repeat(2, 1fr);
  }

  /* 33% - 3 columns */
  .employee-list.is-third > ul {
    @screen tablet {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  /* 25% - 4 columns */
  .employee-list.is-quarter > ul {
    @screen tablet {
      grid-template-columns: repeat(3, 1fr);
    }
    @screen desktop {
      grid-template-columns: repeat(4, 1fr);
    }
  }
}
</style>
